/**
 * DataTable — shared responsive table for list pages.
 *
 * Extracted from the bespoke responsive treatment InvoiceListPage grew in
 * 10a.8 so every list table (Projects, Invoices, future) gets the same
 * behavior from one column-config array instead of hand-wired JSX:
 *
 *  - Progressive column hiding   `responsive: 'md' | 'lg' | 'xl'` hides the
 *    column below that breakpoint (lower-priority columns drop out first).
 *  - Wrapping cells              `wrap: true` overrides the base table's
 *    whitespace-nowrap so long text (names, addresses) takes a second line
 *    instead of forcing a wide single-line column. Pair with `maxWidth`.
 *  - Frozen left columns         `sticky: true` + `stickyLeft: 'lg:left-0'`
 *    keeps identity columns visible during horizontal scroll (lg+ only).
 *    Sticky columns must be the leading columns; each declares its own
 *    cumulative left offset class (Tailwind needs literal classes).
 *  - Sticky mirror scrollbar     a `sticky bottom-0` horizontal scrollbar
 *    pinned to the viewport, rendered only when the table actually
 *    overflows, bidirectionally synced with the real scroll container.
 *
 * JSDOM note: breakpoint hiding is pure CSS, so component tests assert the
 * class wiring; visual behavior is verified via Playwright on staging.
 */
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export type DataTableBreakpoint = 'md' | 'lg' | 'xl';

// Literal class maps — Tailwind's scanner needs to see complete class names.
const RESPONSIVE_CELL: Record<DataTableBreakpoint, string> = {
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
};

export interface DataTableColumn<T> {
  key: string;
  label: React.ReactNode;
  render: (row: T) => React.ReactNode;
  /** Sortable header — requires sortKey; clicking calls onSort(sortKey). */
  sortable?: boolean;
  sortKey?: string;
  align?: 'left' | 'right';
  /** Tailwind width class applied to the header (e.g. 'w-[100px]'). */
  width?: string;
  /** Tailwind max-width class applied to cells (e.g. 'max-w-[220px]'). */
  maxWidth?: string;
  /** Header tooltip (title attribute). */
  tooltip?: string;
  /** Hide this column below the given breakpoint. */
  responsive?: DataTableBreakpoint;
  /** Freeze this column at lg+ during horizontal scroll. Leading columns only. */
  sticky?: boolean;
  /** Literal left-offset class for sticky columns (e.g. 'lg:left-0', 'lg:left-[100px]'). */
  stickyLeft?: string;
  /** Allow cell text to wrap to multiple lines instead of nowrap. */
  wrap?: boolean;
  /** Extra classes for cells in this column. */
  cellClassName?: string;
  /** Cell-level click handler — call e.stopPropagation() to suppress onRowClick. */
  onCellClick?: (row: T, e: React.MouseEvent) => void;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Active sort column (matched against column.sortKey). */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc' | string;
  onSort?: (sortKey: string) => void;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  /** Decorate the first column's cell content (e.g. color dot + badge). */
  firstCellWrapper?: (row: T, content: React.ReactNode) => React.ReactNode;
  /** Rendered in a full-colspan cell when rows is empty. */
  emptyState?: React.ReactNode;
  /** data-testid for the outer wrapper (mirror scrollbar gets `${id}-mirror-scrollbar`). */
  containerTestId?: string;
  /** Classes for the scroll container (defaults to the bordered card look). */
  containerClassName?: string;
  /** Exposes the scroll container element (e.g. for scroll-to-top on page change). */
  scrollContainerRef?: React.MutableRefObject<HTMLDivElement | null>;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  rowClassName,
  firstCellWrapper,
  emptyState,
  containerTestId,
  containerClassName,
  scrollContainerRef,
}: DataTableProps<T>) {
  // 10a.8 machinery (lifted from InvoiceListPage): mirror scrollbar pinned to
  // the viewport bottom so users never have to scroll past every row to reach
  // the horizontal scrollbar.
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const mirrorScrollRef = useRef<HTMLDivElement | null>(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const [tableClientWidth, setTableClientWidth] = useState(0);
  const isSyncing = useRef(false);

  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      tableContainerRef.current = el;
      if (scrollContainerRef) scrollContainerRef.current = el;
    },
    [scrollContainerRef]
  );

  // Track overflow so the mirror scrollbar only renders when needed.
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      setTableScrollWidth(container.scrollWidth);
      setTableClientWidth(container.clientWidth);
    };

    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(container);

    return () => observer.disconnect();
  }, [rows]);

  // Bidirectional scroll sync between table and mirror scrollbar.
  const handleTableScroll = useCallback(() => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    if (mirrorScrollRef.current && tableContainerRef.current) {
      mirrorScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
    isSyncing.current = false;
  }, []);

  const handleMirrorScroll = useCallback(() => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    if (tableContainerRef.current && mirrorScrollRef.current) {
      tableContainerRef.current.scrollLeft = mirrorScrollRef.current.scrollLeft;
    }
    isSyncing.current = false;
  }, []);

  const headerClasses = (col: DataTableColumn<T>) =>
    cn(
      col.align === 'right' && 'text-right',
      col.width,
      col.responsive && RESPONSIVE_CELL[col.responsive],
      col.sticky && cn('lg:sticky lg:bg-background lg:z-10', col.stickyLeft)
    );

  const cellClasses = (col: DataTableColumn<T>) =>
    cn(
      col.align === 'right' && 'text-right',
      col.maxWidth,
      col.responsive && RESPONSIVE_CELL[col.responsive],
      col.sticky && cn('lg:sticky lg:bg-background', col.stickyLeft),
      // tailwind-merge resolves this against the base whitespace-nowrap.
      col.wrap && 'whitespace-normal break-words',
      col.cellClassName
    );

  return (
    <div data-testid={containerTestId}>
      <Table
        containerRef={setContainerRef}
        containerClassName={cn('rounded-md border', containerClassName)}
        onContainerScroll={handleTableScroll}
      >
        <TableHeader>
          <TableRow>
            {columns.map((col) =>
              col.sortable && col.sortKey ? (
                <TableHead
                  key={col.key}
                  className={cn(
                    'cursor-pointer select-none hover:bg-muted/50',
                    headerClasses(col)
                  )}
                  title={col.tooltip}
                  onClick={() => onSort?.(col.sortKey!)}
                >
                  <div
                    className={cn(
                      'flex items-center gap-1',
                      col.align === 'right' && 'justify-end'
                    )}
                  >
                    {col.label}
                    {sortBy === col.sortKey ? (
                      sortOrder === 'asc' ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )
                    ) : (
                      <ChevronsUpDown className="h-4 w-4 text-muted-foreground/50" />
                    )}
                  </div>
                </TableHead>
              ) : (
                <TableHead key={col.key} className={headerClasses(col)} title={col.tooltip}>
                  {col.label}
                </TableHead>
              )
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center">
                {emptyState}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={rowKey(row)}
                className={cn(
                  onRowClick && 'cursor-pointer hover:bg-muted/50',
                  rowClassName?.(row)
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col, colIndex) => (
                  <TableCell
                    key={col.key}
                    className={cellClasses(col)}
                    onClick={col.onCellClick ? (e) => col.onCellClick!(row, e) : undefined}
                  >
                    {colIndex === 0 && firstCellWrapper
                      ? firstCellWrapper(row, col.render(row))
                      : col.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Mirror scrollbar — pinned to viewport bottom, only when overflowing */}
      {tableScrollWidth > tableClientWidth && (
        <div
          ref={mirrorScrollRef}
          data-testid={containerTestId ? `${containerTestId}-mirror-scrollbar` : undefined}
          className="sticky bottom-0 overflow-x-auto bg-background/80 backdrop-blur-sm"
          onScroll={handleMirrorScroll}
        >
          <div style={{ width: tableScrollWidth, height: 1 }} />
        </div>
      )}
    </div>
  );
}
